import { Select as AntSelect, Input, Popconfirm, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import { Icon } from '@components/metrics-builder/components/MetricList';
import useTopPanelStore from '@hooks/useTopPanelStore';
import CancelSvg from '@icons/emp/cancel.svg';
import SaveSvg from '@icons/emp/save.svg';
import CustomDimensionIcon from '@icons/metric-template/metric-builder.svg';
import {
  useCreateCustomDimension,
  useCustomDimensionList,
  useDeleteCustomDimension,
  useUpdateCustomDimension,
} from '@src/emp-3.0/api/queryHooks';
import { Button } from '@src/emp-3.0/components/common/AntdEnhancement';
import CustomDimensionRuleBuilder from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/components/RuleBuilder';
import {
  generateCustomDimensionPayload,
  trimCustomDimensionPayloadMappedEventNames,
  validateCustomDimensionPayload,
} from '@src/emp-3.0/components/custom-dimension/utils';
import { validateDimensionName } from '@src/emp-3.0/domain/dimension';
import { ICreateCustomDimensionPayload, IUpdateCustomDimensionPayload } from '@src/emp-3.0/types';
import { trackDimensionOperation } from '@src/sensor';

import { categoryTypeOptions } from './constant';
import { useCustomDimensionPanelStore } from './store/index';
import {
  Body,
  BodyLeftContainer,
  BodyRightContainer,
  ButtonGroup,
  CustomDimensionDescription,
  DimensionNameCategoryContainer,
  DimensionNameCategoryLabel,
  MismatchNotification,
  Title,
  Toolbar,
  Wrapper,
} from './styled';
import PermissionButton from '@src/emp-3.0/components/PermissionButton';
import { isDeployed } from '@src/emp-3.0/utils/checks';
import classnames from 'classnames';
import { useIsFetching } from '@tanstack/react-query';
import { POST_ATTRIBUTE_TYPE_QUERY_KEY } from '@src/emp-3.0/utils/constants';
import { ALL_APPLICABLE_MAPPED_EVENTS_TYPE } from '@src/emp-3.0/constants/dimensions';
import { renderDeployedStatusDeletePrompt } from '@src/emp-3.0/components/Management/components/CellRenderers/ActionConfirmButton';

export const handleSuccess = (operation: string) => {
  trackDimensionOperation({ type: operation, status: 'success' });
  message.success(`Dimension ${operation === 'edit' ? 'Updated' : 'Created'} Successfully!`);
};

export const handleError = (operation: string) => (error: any) => {
  trackDimensionOperation({ type: operation, status: 'fail' });
  const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
  message.error(errorMessage);
};

const CustomDimensionPanel = () => {
  const isFetchingAttributeTypes =
    useIsFetching({ queryKey: [POST_ATTRIBUTE_TYPE_QUERY_KEY] }) !== 0;

  const { data: customDimensionList } = useCustomDimensionList();
  const { mutate: createCustomDimension, isLoading: isCreateCustomDimensionLoading } =
    useCreateCustomDimension();
  const { mutate: updateCustomDimension, isLoading: isUpdateCustomDimensionLoading } =
    useUpdateCustomDimension();
  const { mutate: deleteCustomDimension, isLoading: isDeleteCustomDimensionLoading } =
    useDeleteCustomDimension();

  const { selectEventType, setSelectEventType, customDimensionPayload, setCustomDimensionPayload } =
    useCustomDimensionPanelStore((state) => state);

  const { resetCustomDimensionPanelStore } = useTopPanelStore();

  const [notificationText, setNotificationText] = useState<string>('');

  const isLoading =
    isCreateCustomDimensionLoading ||
    isUpdateCustomDimensionLoading ||
    isDeleteCustomDimensionLoading;

  const isUpdateDimension = 'custom_dimension_id' in customDimensionPayload;
  const isDeployedDimension = isDeployed(customDimensionPayload);

  const isTraceId = useMemo(() => {
    if (isUpdateDimension) {
      const findDimension = (customDimensionList ?? []).find(
        (dimension) => dimension.id === customDimensionPayload.custom_dimension_id,
      );
      return (
        findDimension &&
        'dimension_identifier' in findDimension &&
        findDimension?.dimension_identifier === 'trace_id'
      );
    } else {
      return false;
    }
  }, [customDimensionPayload, customDimensionList, isUpdateDimension]);

  const isSaveButtonDisabled = useMemo(
    () => !!notificationText || isFetchingAttributeTypes,
    [isFetchingAttributeTypes, notificationText],
  );

  useEffect(() => {
    const { failed, message } = validateDimensionName(customDimensionPayload.display_name);
    if (failed) {
      setNotificationText(message);
    } else {
      setNotificationText('');
    }
  }, [customDimensionPayload.display_name]);

  const handleClickCancel = () => {
    resetCustomDimensionPanelStore();
  };

  const handleClickSave = () => {
    const isAllEvent = selectEventType === ALL_APPLICABLE_MAPPED_EVENTS_TYPE;
    const bypassFields = isAllEvent ? ['mapped_event_names'] : [];
    const { failed, message: validateMessage } = validateCustomDimensionPayload(
      customDimensionPayload,
      bypassFields,
    );
    if (failed) {
      message.warning(validateMessage);
      return;
    }
    const payload: ICreateCustomDimensionPayload | IUpdateCustomDimensionPayload = isAllEvent
      ? generateCustomDimensionPayload({ ...customDimensionPayload, mapped_event_names: [] })
      : trimCustomDimensionPayloadMappedEventNames(
          generateCustomDimensionPayload(customDimensionPayload),
        );
    if ('custom_dimension_id' in payload) {
      updateCustomDimension(
        {
          custom_dimension_id: (payload as IUpdateCustomDimensionPayload).custom_dimension_id,
          queryBody: payload as IUpdateCustomDimensionPayload,
        },
        {
          onSuccess: () => {
            handleSuccess('edit');
            handleClickCancel();
          },
          onError: handleError('edit'),
        },
      );
    } else {
      createCustomDimension(
        { queryBody: payload },
        {
          onSuccess: () => {
            handleSuccess('create');
            handleClickCancel();
          },
          onError: handleError('create'),
        },
      );
    }
  };

  const handleDeleteCustomDimension = () => {
    if (isUpdateDimension) {
      deleteCustomDimension(customDimensionPayload.custom_dimension_id, {
        onSuccess: () => {
          handleClickCancel();
          message.success('Dimension Deleted Successfully!');
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
          message.error(errorMessage);
        },
      });
    }
  };

  const handleChange = (value: { [key: string]: string | string[] | number }) => {
    setCustomDimensionPayload(value);
  };

  return (
    <Wrapper>
      {/* Toolbar section */}
      <Toolbar>
        <Title>
          <Icon component={CustomDimensionIcon} style={{ color: '#494949', fontSize: '20px' }} />
          <span>Custom Dimension</span>
        </Title>
        <ButtonGroup>
          {isUpdateDimension && !isTraceId ? (
            <>
              <PermissionButton styleType="default" onConfirm={handleDeleteCustomDimension}>
                <Popconfirm
                  key="Delete Custom Dimension"
                  title={renderDeployedStatusDeletePrompt()}
                  okText={isDeployedDimension ? 'Undeploy & Delete' : 'Delete'}
                  cancelButtonProps={{ type: 'default' }}
                  okButtonProps={{
                    danger: true,
                    type: 'ghost',
                    style: {
                      border: '1px solid #d0021b',
                      color: '#d0021b',
                    },
                  }}
                  icon={false}
                >
                  <Button className="delete-btn" danger loading={isLoading}>
                    Delete
                  </Button>
                </Popconfirm>
              </PermissionButton>
              <div
                style={{
                  width: '1px',
                  height: '24px',
                  background: '#D9D9D9',
                  margin: '0 7px',
                }}
              />
            </>
          ) : null}
          <PermissionButton onClick={handleClickSave}>
            <Button
              loading={isLoading}
              icon={<Icon component={SaveSvg} />}
              className={classnames('save-btn', isSaveButtonDisabled ? 'disabled' : null)}
              disabled={isSaveButtonDisabled}
            >
              Save
            </Button>
          </PermissionButton>
          <Button
            icon={<Icon component={CancelSvg} />}
            className="cancel-btn"
            onClick={handleClickCancel}
          >
            Cancel
          </Button>
        </ButtonGroup>
      </Toolbar>
      <Body>
        {/* Left section */}
        <BodyLeftContainer>
          <CustomDimensionDescription>
            Select or create a Tag Key and map it to a new dimension
          </CustomDimensionDescription>
          <CustomDimensionRuleBuilder
            eventTag={customDimensionPayload.attribute_name}
            eventNames={customDimensionPayload.mapped_event_names}
            attributeType={customDimensionPayload.attribute_type}
            selectEventType={selectEventType}
            promoteAsGlobal={customDimensionPayload.is_promoted_as_global}
            dimensionIdentifier={customDimensionPayload.dimension_identifier}
            onEventTagChange={(value) => handleChange({ attribute_name: value })}
            onEventNamesChange={(value) => handleChange({ mapped_event_names: value })}
            onAttributeTypeChange={(value) => handleChange({ attribute_type: value })}
            onCategoryChange={(value) => handleChange({ category: value })}
            onSelectEventTypeChange={(value) => setSelectEventType(value)}
            onPromoteAsGlobalChange={(value) => {
              handleChange({
                is_promoted_as_global: value,
              });
            }}
          />
        </BodyLeftContainer>
        {/* Right section */}
        <BodyRightContainer>
          <CustomDimensionDescription>Set Custom Dimension</CustomDimensionDescription>
          <DimensionNameCategoryContainer>
            <div>
              <DimensionNameCategoryLabel>Dimension Category *</DimensionNameCategoryLabel>
              <AntSelect
                disabled
                style={{ width: '100%' }}
                options={categoryTypeOptions}
                value={customDimensionPayload.category}
                onChange={(value) => {
                  handleChange({ category: value });
                }}
                placeholder="Category auto-filled with tag key selection."
              />
            </div>
            <div>
              <DimensionNameCategoryLabel>Dimension Name *</DimensionNameCategoryLabel>
              <Input
                style={{ width: '100%' }}
                placeholder="Please input..."
                value={customDimensionPayload.display_name}
                onChange={(e) => {
                  const value = e.target.value;
                  handleChange({ display_name: value });
                }}
              />
            </div>
            {notificationText && (
              <MismatchNotification>
                <span>{notificationText}</span>
              </MismatchNotification>
            )}
          </DimensionNameCategoryContainer>
        </BodyRightContainer>
      </Body>
    </Wrapper>
  );
};

export default CustomDimensionPanel;
